import React, { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { useConsole } from "../lib/console";
import { attackLabel, fmtDateTime, plural, sevLabel } from "../lib/format";
import { Badge, Button, Callout, Drawer, Modal, SevBadge, SkeletonRows, useToast } from "./ui";
import { Icon } from "./Icon";
import "./InvestigateAI.css";

const VERDICT = {
  likely_malicious: { label: "Likely malicious", tone: "critical" },
  suspicious: { label: "Suspicious", tone: "medium" },
  likely_benign: { label: "Likely benign", tone: "ok" },
  insufficient_data: { label: "Not enough evidence", tone: "neutral" },
};
const STEPS = ["Retrieving evidence", "Enriching indicators", "Assessing uncertainty"];

/** A citation that jumps to the evidence row it points at. */
function Cite({ id, onJump }) {
  return (
    <button type="button" className="ai-cite" onClick={() => onJump(id)} title={`Show evidence ${id}`}>
      {id}
    </button>
  );
}

function Pipeline({ r }) {
  const ctx = r.context;
  const feed = ctx.threat_feed;
  return (
    <ol className="ai-steps" aria-label="How this case was built">
      <li>
        <b>1 · Evidence</b>
        <span>
          {plural(ctx.total_events_in_window, "detection")}
          {ctx.events_shown < ctx.total_events_in_window ? `, newest ${ctx.events_shown} shown` : ""}
        </span>
      </li>
      <li>
        <b>2 · Enrichment</b>
        <span>
          {feed.listed ? `Listed by ${feed.source || "a threat feed"}` : "Not in loaded threat feeds"} · {[ctx.city, ctx.country].filter(Boolean).join(", ") || "location unknown"}
          {ctx.vpn_or_proxy ? " · VPN or proxy" : ""}
        </span>
      </li>
      <li>
        <b>3 · Uncertainty</b>
        <span>
          {r.case.confidence} confidence
          {ctx.simulated_events ? ` · ${ctx.simulated_events} simulated` : ""}
        </span>
      </li>
    </ol>
  );
}

/**
 * Cited investigation of one source. With `saved`, shows a stored case; otherwise runs a new one.
 * The investigator is read-only: blocking happens only when the analyst approves it here.
 */
export default function InvestigateDrawer({ ip, saved, onClose, onSaved }) {
  const c = useConsole();
  const toast = useToast();
  const [r, setR] = useState(saved || null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [focus, setFocus] = useState(null);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [didBlock, setDidBlock] = useState(false);
  const [savedId, setSavedId] = useState(saved ? "saved" : null);
  const evRef = useRef(null);

  const run = () => {
    setR(null);
    setError(null);
    setSavedId(null);
    setStep(0);
    api
      .post("/api/ai/investigate", { source_ip: ip })
      .then((res) => setR(res.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };

  useEffect(() => {
    if (!saved) run();
    // eslint-disable-next-line
  }, [ip]);

  // Cosmetic stage indicator while the request is in flight.
  useEffect(() => {
    if (r || error) return undefined;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 1400);
    return () => clearInterval(t);
  }, [r, error]);

  const jump = (id) => {
    setFocus(id);
    requestAnimationFrame(() => evRef.current?.querySelector(`[data-ev="${id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
  };

  const blocked = c.blockedIPs.has(ip) || didBlock;
  const rec = r?.case.recommendation;
  const verdict = r && (VERDICT[r.case.verdict] || VERDICT.insufficient_data);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post("/api/ai/block", { source_ip: ip, generated_at: r.generated_at });
      toast(`${ip} blocked`, "ok", { detail: "Approved by you and recorded in the audit trail." });
      setDidBlock(true);
      setConfirm(false);
    } catch (e) {
      toast("Couldn't block this source", "critical", { detail: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post("/api/ai/cases", { source_ip: ip, generated_at: r.generated_at, note });
      setSavedId(res.data.id);
      toast("Case saved", "ok", { detail: "Find it under Investigations > AI cases." });
      onSaved?.();
    } catch (e) {
      toast("Couldn't save the case", "critical", { detail: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      width={640}
      title="Investigate with AI"
      subtitle={ip}
      icon={
        <span className="sev-glyph tone-brand">
          <Icon name="sparkle" size={17} />
        </span>
      }
      footer={
        <>
          {!saved && r && (
            <Button variant="ghost" icon="refresh" onClick={run}>
              Re-run
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error ? (
        <Callout tone="critical" icon="alert" title="Couldn't investigate this source">
          {error}
        </Callout>
      ) : !r ? (
        <div>
          <ol className="ai-loading">
            {STEPS.map((s, i) => (
              <li key={s} className={i < step ? "is-done" : i === step ? "is-now" : ""}>
                {s}
              </li>
            ))}
          </ol>
          <SkeletonRows rows={4} />
        </div>
      ) : (
        <>
          <div className="row row-wrap" style={{ marginBottom: 14, gap: 18 }}>
            <Badge tone={verdict.tone} size="lg">
              {verdict.label}
            </Badge>
            <Badge tone="neutral" size="lg">
              {r.case.confidence} confidence
            </Badge>
            <SevBadge sev={r.context.worst_severity} size="lg" />
            <Badge tone={r.engine === "ai" ? "brand" : "neutral"} size="lg" title={r.model}>
              {r.engine === "ai" ? "AI model" : "Fixed rules"}
            </Badge>
          </div>

          {r.note && (
            <Callout tone="medium" icon="info" className="drawer-section">
              {r.note}
            </Callout>
          )}

          <p className="ai-summary">{r.case.summary}</p>
          <Pipeline r={r} />

          <div className="drawer-section">
            <h3>What happened</h3>
            <ol className="ai-timeline">
              {r.case.timeline.map((t, i) => (
                <li key={i}>
                  <span className="ai-cites">
                    {t.evidence.map((id) => (
                      <Cite key={id} id={id} onJump={jump} />
                    ))}
                  </span>
                  <span>{t.text}</span>
                </li>
              ))}
            </ol>
            {r.case.dropped_claims > 0 && (
              <p className="small muted">{plural(r.case.dropped_claims, "statement")} removed because they did not cite real evidence.</p>
            )}
          </div>

          <div className="drawer-section">
            <h3>How sure are we</h3>
            <p className="small dim" style={{ margin: "0 0 8px" }}>
              {r.case.confidence_reason}
            </p>
            {r.case.unknowns.length > 0 && (
              <ul className="ai-unknowns">
                {r.case.unknowns.map((u, i) => (
                  <li key={i}>{u}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="drawer-section">
            <h3>Recommendation</h3>
            <div className={`ai-rec ai-rec-${rec.action}`}>
              <div>
                <strong>{blocked ? "This source is already blocked" : rec.action === "block" ? "Block this source" : rec.action === "monitor" ? "Keep monitoring" : "No action needed"}</strong>
                <p>
                  {rec.rationale}{" "}
                  {rec.evidence.map((id) => (
                    <Cite key={id} id={id} onJump={jump} />
                  ))}
                </p>
                <p className="small muted">Nothing happens until you approve. The platform never blocks from this panel on its own.</p>
              </div>
              {!saved && rec.action === "block" && !blocked && (
                <Button variant="danger" icon="lock" onClick={() => setConfirm(true)}>
                  Approve block
                </Button>
              )}
            </div>
          </div>

          <div className="drawer-section">
            <h3>Evidence</h3>
            <div className="ai-evidence" ref={evRef}>
              {r.evidence.map((e) => (
                <div key={e.id} data-ev={e.id} className={focus === e.id ? "ai-ev is-focus" : "ai-ev"}>
                  <span className="ai-ev-id">{e.id}</span>
                  <div>
                    <div>
                      {attackLabel(e.attack_type)} · <span className="ai-sev" style={{ color: `var(--sev-${String(e.severity).toLowerCase()})` }}>{sevLabel(e.severity)}</span>
                      <span className="muted"> · {e.origin === "real" ? "real attempt" : "simulated"}</span>
                    </div>
                    <div className="small muted">
                      {fmtDateTime(e.time)} · {e.protocol} to {e.target}
                      {e.mitre ? ` · ${e.mitre}` : ""}
                    </div>
                    {e.detail && <div className="ai-ev-detail">{e.detail}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!saved && (
            <div className="drawer-section">
              <h3>Save as a case</h3>
              {savedId ? (
                <Callout tone="ok" icon="checkCircle">
                  Saved with its evidence. Open it any time under Investigations, AI cases.
                </Callout>
              ) : (
                <div className="col" style={{ gap: 8 }}>
                  <textarea className="textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note: what you decided and why" aria-label="Case note" maxLength={2000} />
                  <div className="row" style={{ justifyContent: "flex-end" }}>
                    <Button icon="plus" loading={busy} onClick={save}>
                      Save case
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {saved?.analyst_note && (
            <div className="drawer-section">
              <h3>Analyst note</h3>
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{saved.analyst_note}</p>
            </div>
          )}
        </>
      )}

      <Modal open={confirm} onClose={() => setConfirm(false)} label="Approve block">
        <div className="modal-head">
          <h2>Block {ip}?</h2>
          <p>All further traffic from this source is dropped until you release it. This is recorded in the audit trail as approved by you.</p>
        </div>
        <div className="modal-foot" style={{ marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" icon="lock" loading={busy} onClick={approve} data-autofocus>
            Block source
          </Button>
        </div>
      </Modal>
    </Drawer>
  );
}
